import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PATH_PROJECTS, PATH_STORES, PATH_CONTRACTORS } from "@/constants";
import { useFormatters } from "@/lib/hooks/useFormatters";
import { ExternalLink } from "lucide-react";
import { openNewWindow } from "@/lib/utils";
import { useContractorCardData } from "../hooks";
import { CardRequireBadge } from "@/modules/contractor-panel/cards/shared/components";
import { Section } from "@/components/details";
import { readContractorCard } from "@/schemas/contractorCard";

const CONTRACTOR_CARDS_I18N_MODULE = "contractor-cards";

export const ContractorCardDetails = () => {
  const { t } = useTranslation();
  const contractor = useContractorCardData();
  const { formatDateTime } = useFormatters();

  const handleOpenProject = useCallback(() => {
    openNewWindow(`${PATH_PROJECTS}/${contractor.project_id}`);
  }, [contractor.project_id]);

  const handleOpenStore = useCallback(() => {
    openNewWindow(`${PATH_STORES}/${contractor.store_id}`);
  }, [contractor.store_id]);

  const handleOpenContractor = useCallback(() => {
    openNewWindow(`${PATH_CONTRACTORS}/${contractor.contractor_id}`);
  }, [contractor.contractor_id]);

  return (
    <Section
      variant="card"
      className="gap-6"
      module={CONTRACTOR_CARDS_I18N_MODULE}
      schema={readContractorCard}
      data={contractor}
    >
      <Section.Field
        field="project_name"
        label={
          <span className="flex items-center gap-1">
            {t("contractorCards.fields.project_name")}
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4"
              onClick={handleOpenProject}
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
          </span>
        }
      />
      <Section.Field
        field="store_name"
        label={
          <span className="flex items-center gap-1">
            {t("contractorCards.fields.store_name")}
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4"
              onClick={handleOpenStore}
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
          </span>
        }
      />
      <Section.Field
        field="contractor_name"
        label={
          <span className="flex items-center gap-1">
            {t("contractorCards.fields.contractor_name")}
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4"
              onClick={handleOpenContractor}
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
          </span>
        }
      />

      <Section.Field field="period" />
      <Section.Field
        field="contractor_card_required"
        value={
          <CardRequireBadge required={contractor.contractor_card_required} />
        }
      />
      <Section.Field
        field="card_filename"
        value={contractor.card_filename || "-"}
      />
      <Section.Field
        field="card_mimetype"
        value={
          contractor.card_mimetype ? (
            <Badge variant="outline">{contractor.card_mimetype}</Badge>
          ) : (
            "-"
          )
        }
      />
      <Section.Field
        field="created_at"
        value={
          contractor.created_at ? formatDateTime(contractor.created_at) : "-"
        }
      />
      <Section.Field
        field="updated_at"
        value={
          contractor.updated_at ? formatDateTime(contractor.updated_at) : "-"
        }
      />
    </Section>
  );
};
